/**
 * head-tracker.js — velocity estimation for a tracked point on a held object.
 *
 * `core/grab.js` estimates the velocity of the HAND at the moment of release.
 * A mallet needs the same treatment applied to a different point: the head,
 * which is what actually reaches the pad, and whose speed is what High Striker
 * scores (GDD 6.6.1). This is that estimator, in the same shape as GDD 5.3 —
 * ring-buffer the world pose per frame, differentiate consecutive samples, and
 * take an exponentially recency-weighted average of the most recent few.
 *
 * Kept separate from the stall so the smoothing is testable on its own terms
 * and so a second mallet stall can reuse it rather than copy it.
 *
 * @module stalls/head-tracker
 */

import * as THREE from 'three';

export class HeadTracker {
  /**
   * @param {{ bufferSize?: number, smoothFrames?: number, weightDecay?: number }} [opts]
   */
  constructor({ bufferSize = 12, smoothFrames = 5, weightDecay = 0.72 } = {}) {
    this.bufferSize = bufferSize;
    this.smoothFrames = smoothFrames;
    this.weightDecay = weightDecay;
    /** @type {Array<{p: THREE.Vector3, t: number}>} Oldest first. */
    this.samples = [];
  }

  /** Forget every sample — call when a round starts. */
  reset() {
    this.samples.length = 0;
  }

  /**
   * Record a world-space pose.
   * @param {THREE.Vector3} worldPos
   * @param {number} time Seconds, monotonically increasing.
   */
  push(worldPos, time) {
    this.samples.push({ p: worldPos.clone(), t: time });
    if (this.samples.length > this.bufferSize) this.samples.shift();
  }

  /**
   * Weighted rolling average of recent velocities, in world space.
   * @returns {THREE.Vector3} Zero when there is not enough history yet.
   */
  velocity() {
    const s = this.samples;
    const out = new THREE.Vector3();
    if (s.length < 2) return out;

    const vels = [];
    for (let i = 1; i < s.length; i++) {
      const dt = s[i].t - s[i - 1].t;
      if (dt < 1e-5) continue;
      vels.push(s[i].p.clone().sub(s[i - 1].p).divideScalar(dt));
    }
    if (vels.length === 0) return out;

    const window = vels.slice(-this.smoothFrames);
    let weightSum = 0;
    for (let i = 0; i < window.length; i++) {
      // Most recent sample gets weight 1, each older one decays.
      const w = Math.pow(this.weightDecay, window.length - 1 - i);
      out.addScaledVector(window[i], w);
      weightSum += w;
    }
    if (weightSum > 0) out.divideScalar(weightSum);
    return out;
  }
}
